import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CalledFrom } from '../../../models/CalledFromEnum';
import { setFilterButtonText } from '../PortfolioUtil';
import Filters from '../../FilterV2/Filters';
import { ReactComponent as IconFiltersV } from '../../../assets/icons/u_filters-v.svg';
import { ReactComponent as IconFiltersClosed } from '../../../assets/icons/u_chevron-down.svg';
import { ReactComponent as IconFiltersOpen } from '../../../assets/icons/u_chevron-up.svg';
import ProjectTypeFilter from '../../Filter/ProjectTypeFilter/ProjectTypeFilter';
import css from './FilterBar.module.scss';
import { useContainerDimensions } from '../../../hooks/useContainerDimension';
import { TagFilter } from '../../TagFilter/TagFilter';
import { useDynamicTagLayout } from '../../../hooks/useDynamicTagLayout';
import { ReactComponent as IconTrash } from '../../../assets/icons/u_trash.svg';
import { setFilterIndicators } from '../../../redux/actions/filterIndicators';
import { useDispatch } from 'react-redux';
import { EMPTY_EARLY_WARNINGS_FILTER, EMPTY_PORTFOLIO_FILTER } from '../../../mockData/mockObjFilter';
import { DimensionContext } from '../../../context/DimensionContext';
import { TagFilterContext } from '../../../context/TagFilterContext';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import FilterBubble from '../../Filter/FilterBubble/FilterBubble';
import { setFilter } from '../../../redux/actions/filter';
import FilterProjects from '../../../models/FilterProjects';
import { getInitialFilteredTags } from './HandleFiltersData/HandleFiltersData';
import { getFilter, getSelectorFilter } from '../../FilterV2/UtilFunctions';
import { setFilterEarlyWarnings } from '../../../redux/actions/filterEarlyWarnings';

interface Props {
  hideFilter?: boolean;
  hideFilterProjectType?: boolean;
  calledFrom?: CalledFrom;
  indicatorCall?: boolean;
}

const Filter = ({
  hideFilter,
  hideFilterProjectType,
  calledFrom = CalledFrom.PORTFOLIO,
  indicatorCall = true
}: Props) => {
  const { t } = useTranslation();

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [isMoreFiltersHovered, setIsMoreFiltersHovered] = useState(false);

  const dropMenuRef = useRef(null);

  const { tagDimension, setTagDimension } = useContext(DimensionContext);
  const { labels, setTagOptions } = useContext(TagFilterContext);

  const filter = useSelector<any, FilterProjects>(state => getFilter(state, calledFrom));
  const selectorFilter = useSelector<any, FilterProjects>(state => getSelectorFilter(state, calledFrom));
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);

  const { containerRef, dimensions } = useContainerDimensions();
  const { visibleTags, overflowTags } = useDynamicTagLayout();

  const dispatch = useDispatch();

  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;

  const currentTagNames = useMemo(
    () => new Set(tagDimension.map(tag => tag.name)),
    [tagDimension, portfolioSelectedTab],
  );

  // TODO: Confirmar si debo quitar la funcionalidad de cerrar el filtro si doy click por fuera
  //useOutsideClick(dropMenuRef, () => setShowFilters(false));

  useEffect(() => {
    getInitialFilteredTags(setTagOptions, selectorFilter);
  }, []);

  useEffect(() => {
    const currentTagNamesArray = Array.from(currentTagNames);

    const newTags = labels.filter(label => !currentTagNames.has(label));
    const removedTags = currentTagNamesArray.filter(name => !labels.includes(name));

    if (newTags.length > 0 || removedTags.length > 0) {
      setTagDimension(prevDimensions => {
        const updatedDimensions = prevDimensions.filter(tag => !removedTags.includes(tag.name));
        return [...updatedDimensions, ...newTags.map(label => ({ width: 0, height: 0, name: label }))];
      });
    }
  }, [labels, currentTagNames, setTagDimension]);

  const toggleShowFilters = () => setShowFilters(prev => !prev);
  const handleMouseEnter = useCallback(() => setIsMoreFiltersHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsMoreFiltersHovered(false), []);

  const cleanAllTags = (calledFrom) => {
    setShowFilters(true);

    switch (calledFrom) {
      case CalledFrom.PORTFOLIO:
        dispatch(
          setFilterIndicators({
            ...EMPTY_PORTFOLIO_FILTER.common,
            common: {
              ...EMPTY_PORTFOLIO_FILTER.common,
            },
          }),
        );
        break;

      case CalledFrom.PROJECT:
        dispatch(
          setFilter({
            ...filter,
            allProjectsFilter: {
              ...filter.allProjectsFilter,
              searchValue: '',
            },
            common: {
              ...EMPTY_PORTFOLIO_FILTER.common,
            },
            allProjectsPagination: {
              ...filter.allProjectsPagination,
              pageSize: 9,
              currentPage: 1,
            },
          }),
        );
        break;

      case CalledFrom.EARLY_WARNINGS:
        dispatch(
          setFilterEarlyWarnings({
            ...filter,
            common: {
              ...EMPTY_EARLY_WARNINGS_FILTER.common,
            }
          }),
        );
        break;

      default:
        break;
    }
  };

  return (
    <div ref={dropMenuRef} className={`${css['Filters']} ${showFilters ? css['show-filters'] : css['hide-filters']}`}>
      <div className={css['container-filter']}>
        {!hideFilter && (
          <button className={`btn-primary btn-filter ${css['btn-filter']}`} onClick={() => toggleShowFilters()}>
            <IconFiltersV className={css['icon-color']} />
            {setFilterButtonText(t)}
            {showFilters ? (
              <IconFiltersOpen className={css['icon-color']} />
            ) : (
              <IconFiltersClosed className={css['icon-color']} />
            )}
          </button>
        )}
        <div ref={containerRef} className={css['c-tag']}>
          {calledFrom === CalledFrom.PORTFOLIO && dimensions && visibleTags.map(tag => <TagFilter key={tag.name} tag={tag.name} calledFrom={calledFrom} />)}
          {(calledFrom === CalledFrom.PROJECT || calledFrom === CalledFrom.EARLY_WARNINGS) && <FilterBubble indicatorCall={indicatorCall} calledFrom={calledFrom} />}
          {overflowTags.length > 0 && (
            <p className={css['more-filters-text']} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              {`+${overflowTags.length} filtros`}
            </p>
          )}
          {visibleTags.length > 0 && (
            <div className={css['clean-filter']} onClick={() => cleanAllTags(calledFrom)}>
              <IconTrash /> {t('clean')}{' '}
            </div>
          )}
        </div>

        <ul className={`${css['c-hidden-filters']} ${isMoreFiltersHovered ? css['show-hidden-filters'] : ''}`}>
          {overflowTags.map(item => (
            <li key={item.name}>{item.name}</li>
          ))}
        </ul>

        {!hideFilterProjectType && (
          <div className={css['filters-button']}>
            <ProjectTypeFilter filterTitle="the_portfolio_consists_of" indicatorCall={indicatorCall} />
          </div>
        )}
      </div>
      {showFilters && <Filters indicatorCall={indicatorCall} calledFrom={calledFrom} />}
    </div>
  );
};

export default Filter;
